import { lazy } from "react";

/**
 * Identify if an error is a Webpack / Vite dynamic chunk import failure.
 *
 * @param {Error|*} error
 * @return {boolean}
 */
export const isChunkLoadError = error => {
  if (!error) return false;
  const name = error.name || "";
  const message = error.message || "";
  return (
    name === "ChunkLoadError" ||
    /Loading (CSS )?chunk \d+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message)
  );
};

/**
 * Storage key used to guard against infinite page refresh loops during chunk load failures.
 *
 * @const
 * @type {string}
 */
export const CHUNK_RELOAD_KEY = "orderlink_chunk_reload_guard";

/**
 * Safely access window.sessionStorage, guarding against SecurityError in strict environments.
 *
 * @return {Storage|null}
 */
const getSafeSessionStorage = () => {
  try {
    return typeof window !== "undefined" && window.sessionStorage ? window.sessionStorage : null;
  } catch {
    return null;
  }
};

/**
 * React.lazy wrapper that automatically intercepts ChunkLoadError (caused by
 * a new production deployment pruning old asset hashes) and triggers a single
 * safe page refresh to download the latest bundle manifest without trapping
 * the user in an unrecoverable crash loop.
 *
 * @param {function(): Promise<{default: React.ComponentType<*>}>} componentImport
 * @return {React.LazyExoticComponent<React.ComponentType<*>>}
 */
export const lazyWithRetry = componentImport =>
  lazy(async () => {
    const storage = getSafeSessionStorage();
    const pageAlreadyRefreshed = storage
      ? storage.getItem(CHUNK_RELOAD_KEY) === "true"
      : false;

    try {
      const component = await componentImport();
      if (storage) {
        storage.removeItem(CHUNK_RELOAD_KEY);
      }
      return component;
    } catch (error) {
      if (isChunkLoadError(error)) {
        if (storage && !pageAlreadyRefreshed) {
          storage.setItem(CHUNK_RELOAD_KEY, "true");
          window.location.reload();
          // Return a pending promise so React Suspense waits for page unload
          return new Promise(() => {});
        }
      }
      throw error;
    }
  });
